// Studby — Stripe payment success page. Polls status then refreshes the user.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | timeout | error
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let count = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("success");
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStatus("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      count += 1;
      if (count >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-background px-5">
      <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-accent mx-auto mb-4" aria-hidden="true" />
            <h1 className="font-heading text-xl font-bold">Confirming your subscription…</h1>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 size={48} className="text-accent mx-auto mb-4" aria-hidden="true" />
            <h1 className="font-heading text-2xl font-bold mb-2">You're all set! 🎉</h1>
            <p className="text-muted-foreground mb-6">Welcome to Studby Pro. Time to study smarter.</p>
            <Link to="/app/dashboard">
              <Button className="rounded-full w-full" data-testid="go-dashboard-button">Go to dashboard</Button>
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle size={44} className="text-destructive mx-auto mb-4" aria-hidden="true" />
            <h1 className="font-heading text-xl font-bold mb-2">Still processing</h1>
            <p className="text-muted-foreground mb-6">
              Your payment may still be confirming. Check your dashboard in a moment.
            </p>
            <Link to="/app/dashboard">
              <Button variant="outline" className="rounded-full w-full">Go to dashboard</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
